import { NumberInput, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import type { IncomingInvoiceReviewFormSchema } from "@msuite/utils/schemas";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { formatInvoiceAmount } from "@/components/incoming-invoices";

/** Props Interface */
interface ReviewReductionFieldsProps {
	printedNetAmount: number | null;
	currency: string;
}

/**
 * The Kürzung, shown while the review is set to "Kürzen": the approver names the net amount the
 * invoice is paid on instead of the printed one, and says why.
 */
export function ReviewReductionFields({ printedNetAmount, currency }: ReviewReductionFieldsProps) {
	/** Context */
	const methods = useFormContext<IncomingInvoiceReviewFormSchema>();

	/** Hooks */
	const { t } = useTranslation();
	const isReduced = useWatch({ control: methods.control, name: "isReduced" });

	/** Guard */
	if (!isReduced) return null;

	/** Render */
	return (
		<React.Fragment>
			<p className="label">{t("incoming_invoices.decision.reduce_hint")}</p>
			<NumberInput
				control={methods.control}
				name="reducedNetAmount"
				label={t("incoming_invoices.decision.reduced_net_amount")}
				variant="currency"
				hideButtons
				isRequired
				max={printedNetAmount ?? undefined}
				helperText={`${t("incoming_invoices.decision.printed_net_amount")}: ${formatInvoiceAmount(printedNetAmount, currency)}`}
			/>
			<TextInput
				control={methods.control}
				name="reductionReason"
				label={t("incoming_invoices.decision.reduction_reason")}
				isRequired
				data-cy="incoming-invoice-review-reduction-reason-input"
			/>
		</React.Fragment>
	);
}
